import type {
  InitGameOptions,
  YachtCategory,
  YachtTurnModeConfig,
  YachtViewerTurnState,
} from "@cloudnest/yacht-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { buildLobbyPath, navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import { DesktopYachtScreen } from "./desktop/DesktopYachtScreen";
import { MobileYachtScreen } from "./mobile/MobileYachtScreen";
import {
  useYachtMultiplayerStore,
  yachtMultiplayerActions,
} from "./multiplayer-game-store";
import "./styles/yacht-tokens.css";

const DEFAULT_YACHT_GAME_TYPE = "yacht";
const YACHT_RACE_GAME_TYPE = "yacht-race";

type ScreenMode = "mobile" | "desktop";

function resolveScreenMode(): ScreenMode {
  if (typeof window === "undefined") return "mobile";
  return window.matchMedia("(min-width: 768px)").matches ? "desktop" : "mobile";
}

function getTurnMode(config: Record<string, unknown>): YachtTurnModeConfig {
  return config.turnMode === "SEQUENTIAL" ||
    config.turnMode === "SIMULTANEOUS" ||
    config.turnMode === "auto"
    ? config.turnMode
    : "auto";
}

function resolveTurnModeForGameType(
  gameType: string,
  config: Record<string, unknown>,
): YachtTurnModeConfig {
  if (gameType === YACHT_RACE_GAME_TYPE) return "SIMULTANEOUS";
  if (gameType === DEFAULT_YACHT_GAME_TYPE) return "SEQUENTIAL";
  return getTurnMode(config);
}

export function GameScreen() {
  const [screenMode] = useState(resolveScreenMode);
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError, myPlayerId, isObserver } =
    useYachtMultiplayerStore();
  const actionsRef = useRef(yachtMultiplayerActions);
  const lobbyActionsRef = useRef(lobbyActions);
  const roomId = currentRoom?.id ?? null;
  const activeGameType = currentRoom?.gameType ?? DEFAULT_YACHT_GAME_TYPE;
  const userId = user?.id ?? null;
  const hostUserId = currentRoom?.hostUserId ?? null;
  const participantUserIds = useMemo(
    () => currentRoom?.participantUserIds ?? [],
    [currentRoom?.participantUserIds],
  );
  const participantUserIdsKey = participantUserIds.join("|");
  const isHost = hostUserId === userId;
  const isRoomObserver = Boolean(
    userId &&
      (currentRoom?.observerUserIds ?? []).includes(userId) &&
      !participantUserIds.includes(userId),
  );
  const readOnlyObserver = isObserver || isRoomObserver;

  useEffect(() => {
    actionsRef.current = yachtMultiplayerActions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  useGameEventsWS(roomId, {
    onGameEvent: () => {},
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: room id + host bit are the stable init boundaries; room_updated may refresh participant metadata.
  useEffect(() => {
    if (!roomId || !userId || !hostUserId) return;
    const playerIds = participantUserIdsKey.split("|").filter(Boolean);
    if (playerIds.length < 2) return;

    if (readOnlyObserver) {
      void yachtMultiplayerActions.initializeAsObserver(roomId, activeGameType);
    } else if (isHost) {
      const players: InitGameOptions["players"] = playerIds.map(
        (playerId, index) => ({
          playerId,
          displayName: getParticipantDisplayName(
            currentRoom,
            playerId,
            `P${index + 1}`,
          ),
        }),
      );
      void yachtMultiplayerActions.initializeAsHost(
        roomId,
        activeGameType,
        players,
        resolveTurnModeForGameType(activeGameType, currentRoom?.config ?? {}),
      );
    } else {
      void yachtMultiplayerActions.initializeAsGuest(roomId, activeGameType);
    }

    return () => {
      yachtMultiplayerActions.reset();
    };
  }, [
    roomId,
    currentRoom?.id,
    activeGameType,
    userId,
    isHost,
    readOnlyObserver,
  ]);

  function leaveGame(): void {
    yachtMultiplayerActions.reset();
    void lobbyActions.leaveGame();
    navigateTo(buildLobbyPath(activeGameType));
  }

  function submitWindowAction(
    eventType: string,
    slot: YachtViewerTurnState,
    extra: Record<string, unknown> = {},
  ): void {
    void yachtMultiplayerActions.submitAction(eventType, {
      sheetRound: slot.sheetRound,
      turnNumber: slot.turnNumber,
      ...extra,
    });
  }

  if (!currentRoom || !viewerState) {
    return (
      <main className="yacht-root" data-testid="yacht-loading">
        <div className="yacht-shell">
          <section className="yacht-loading-card">
            <strong>Yacht · 요트</strong>
            <span>게임 상태를 불러오는 중입니다.</span>
            {lastError && <span>{lastError.error}</span>}
            <button className="yacht-button" onClick={leaveGame} type="button">
              로비로
            </button>
          </section>
        </div>
      </main>
    );
  }

  const Screen =
    screenMode === "desktop" ? DesktopYachtScreen : MobileYachtScreen;

  return (
    <Screen
      state={viewerState}
      myPlayerId={myPlayerId}
      readOnly={readOnlyObserver}
      onRoll={(slot) => submitWindowAction("ROLL_DICE", slot)}
      onToggleHold={(slot, dieIndex) =>
        submitWindowAction("TOGGLE_HOLD", slot, { dieIndex })
      }
      onConfirmScore={(slot, category: YachtCategory) =>
        submitWindowAction("SCORE_CATEGORY", slot, { category })
      }
      onLeaveGame={leaveGame}
    />
  );
}
